/**
 * Project "hike highlights" (waypoints / POIs) onto an elevation profile line.
 *
 * For each highlight this computes, via turf `nearestPointOnLine`:
 *  - its distance ALONG the line (metres) — where to place it on the x-axis;
 *  - its perpendicular distance FROM the line (metres) — used both to filter
 *    out highlights that are nowhere near the route and to let callers show how
 *    far off-route a highlight is;
 *  - the snapped `[lng, lat]` on the line.
 *
 * Highlights farther than `maxDistanceFromLineMeters` from the line are
 * excluded (they aren't really on this route).
 */
import {
  lineString as turfLineString,
  point as turfPoint,
  nearestPointOnLine,
  distance as turfDistance,
} from '@turf/turf';

export interface HighlightInput {
  id: string | number;
  name: string;
  lon: number;
  lat: number;
  icon?: string;
  /** Any extra caller data is preserved on the output via the generic. */
  [key: string]: unknown;
}

export interface ProjectedHighlight {
  id: string | number;
  name: string;
  icon?: string;
  /** Distance along the line, in metres, where the highlight snaps. */
  distanceMeters: number;
  /** Perpendicular distance from the line to the highlight, in metres. */
  distanceFromLineMeters: number;
  /** `[lng, lat]` snapped onto the line. */
  snappedLngLat: [number, number];
}

export interface ProjectHighlightsOptions {
  /** Drop highlights farther than this from the line (metres). Default 250. */
  maxDistanceFromLineMeters?: number;
  /**
   * Optional true route length (metres). Highlights whose snapped distance
   * exceeds this are clamped to it (guards against float drift past the end).
   */
  maxDistanceMeters?: number;
  /**
   * Optional cumulative distance (metres) parallel to `lineCoords`. Pass it
   * when the profile's distance axis isn't simply the length along the line —
   * e.g. a multi-route profile that restarts at each route instead of
   * measuring across the gap — so highlights land where the chart plots them.
   */
  distances?: number[];
}

/**
 * Coerce one GeoJSON position to a plain `[lon, lat]` pair, or null if it isn't
 * usable.
 *
 * turf is strict here but validates late: `lineString()` accepts any array
 * shape, then `nearestPointOnLine` throws "coordinates must contain numbers"
 * from deep inside its segment walk when it meets a bad position. Note that
 * `typeof NaN === 'number'`, so a plain typeof check is not enough — turf's own
 * `isNumber` rejects NaN, and NaN is exactly what upstream arithmetic on a
 * missing coordinate produces.
 */
function toLngLat(coord: unknown): [number, number] | null {
  if (!Array.isArray(coord) || coord.length < 2) return null;
  const [lon, lat] = coord;
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;
  return [lon as number, lat as number];
}

/**
 * Project highlights onto `lineCoords` (`[lon, lat, …][]`, GeoJSON order).
 * Returns projected highlights sorted by distance along the line, excluding any
 * beyond `maxDistanceFromLineMeters`.
 *
 * Malformed positions in `lineCoords` are dropped rather than thrown on: this
 * runs inside Vue computeds on the hike map and elevation profiles, where one
 * bad point in a route's geometry would otherwise take down the whole render.
 *
 * Repeated consecutive positions are collapsed for the same reason. turf's
 * `nearestPointOnLine` does its segment maths on the sphere, and a zero-length
 * segment gives it a zero cross product to normalise — NaN — which it then
 * feeds to `point()`, which throws "coordinates must contain numbers". Route
 * geometry has these duplicates routinely: the hike profile joins each route's
 * lineStrings end-to-start, and the end of one is usually the start of the
 * next.
 */
export function projectHighlightsOntoLine(
  lineCoords: number[][],
  highlights: HighlightInput[],
  options: ProjectHighlightsOptions = {}
): ProjectedHighlight[] {
  const maxFromLine = options.maxDistanceFromLineMeters ?? 250;

  if (!Array.isArray(lineCoords) || lineCoords.length < 2 || !highlights || highlights.length === 0) {
    return [];
  }

  // turf requires 2D coords; strip any elevation (and any junk) as we go, and
  // collapse runs of identical positions (see the doc comment). `sourceIndex`
  // maps each kept position back to its index in `lineCoords` so a parallel
  // `options.distances` still lines up after positions are dropped.
  const cleanCoords: [number, number][] = [];
  const sourceIndex: number[] = [];
  let malformed = 0;
  lineCoords.forEach((c, i) => {
    const lngLat = toLngLat(c);
    if (!lngLat) {
      malformed += 1;
      return;
    }
    const prev = cleanCoords[cleanCoords.length - 1];
    if (prev && prev[0] === lngLat[0] && prev[1] === lngLat[1]) return;
    cleanCoords.push(lngLat);
    sourceIndex.push(i);
  });

  if (malformed > 0) {
    // Worth knowing about: it means a route's geometry reached the client with
    // holes in it. Projection carries on with what's left.
    console.warn(
      `[projectHighlightsOntoLine] dropped ${malformed} of ` +
      `${lineCoords.length} line positions that were not [lon, lat] number pairs`
    );
  }

  // A single surviving point is not a line turf can snap to.
  if (cleanCoords.length < 2) return [];

  const line = turfLineString(cleanCoords);
  // Only trustworthy while it lines up index-for-index with the input line.
  // A dropped duplicate shares its predecessor's cumulative distance, so
  // mapping through `sourceIndex` stays exact.
  const useDistances = Array.isArray(options.distances) &&
    options.distances.length === lineCoords.length;

  const projected: ProjectedHighlight[] = [];

  for (const h of highlights) {
    if (h == null || !Number.isFinite(h.lon) || !Number.isFinite(h.lat)) {
      continue;
    }

    const snapped = nearestPointOnLine(line, turfPoint([h.lon, h.lat]), {
      units: 'meters',
    });

    const distanceFromLineMeters = snapped.properties.dist ?? Infinity;
    if (distanceFromLineMeters > maxFromLine) continue;

    let distanceMeters = snapped.properties.location ?? 0;
    const segmentIndex = snapped.properties.index;
    if (useDistances && segmentIndex != null && segmentIndex < cleanCoords.length) {
      distanceMeters = options.distances![sourceIndex[segmentIndex]] + turfDistance(
        cleanCoords[segmentIndex], snapped.geometry.coordinates as [number, number], { units: 'meters' }
      );
    }
    if (options.maxDistanceMeters != null && distanceMeters > options.maxDistanceMeters) {
      distanceMeters = options.maxDistanceMeters;
    }

    const [lng, lat] = snapped.geometry.coordinates;

    projected.push({
      id: h.id,
      name: h.name,
      icon: h.icon,
      distanceMeters,
      distanceFromLineMeters,
      snappedLngLat: [lng, lat],
    });
  }

  projected.sort((a, b) => a.distanceMeters - b.distanceMeters);
  return projected;
}
